import type { LeafEntry } from "./confirmationCardFormatting";
import { offeredProcedures, type ProcedureInputs } from "../procurement/procedureAvailability";

export type EditorKind =
  | "text"
  | "longText"
  | "number"
  | "money"
  | "date"
  | "dateTime"
  | "yesNo"
  | "choice"
  | "readOnly";

export type Money = { value: number | null; currency: string };

export const isMoney = (value: unknown): value is Money =>
  !!value &&
  typeof value === "object" &&
  !Array.isArray(value) &&
  typeof (value as Money).currency === "string" &&
  ((value as Money).value === null || typeof (value as Money).value === "number") &&
  Object.keys(value).every((key) => key === "value" || key === "currency");

type Schema = Record<string, unknown> | undefined;

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const PROSE_KEY = /(description|justification|rationale|note)s?$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const variantsOf = (node: Schema): Record<string, unknown>[] =>
  node ? [node, ...((node.allOf as Record<string, unknown>[] | undefined) ?? [])] : [];

export const schemaAt = (schema: Schema, path: string[]): Schema =>
  path.reduce<Schema>(
    (node, key) =>
      variantsOf(node)
        .map((variant) => (variant.properties as Record<string, Schema> | undefined)?.[key])
        .find((property) => property !== undefined),
    schema,
  );

export const isRequiredLeaf = (schema: Schema, path: string[]): boolean => {
  const parent = schemaAt(schema, path.slice(0, -1));
  const leaf = schemaAt(schema, path);
  const listedAsRequired = ((parent?.required as string[] | undefined) ?? []).includes(path[path.length - 1]);
  return listedAsRequired || (leaf !== undefined && leaf.nullable !== true);
};

export const choicesOf = (schema: Schema, path: string[]): string[] =>
  variantsOf(schemaAt(schema, path))
    .flatMap((variant) => (variant.enum as unknown[] | undefined) ?? [])
    .filter((value): value is string => typeof value === "string");

export const editorKindFor = (entry: LeafEntry, schema: Schema): EditorKind => {
  const key = entry.path[entry.path.length - 1];
  const leafSchema = schemaAt(schema, entry.path);
  const value = entry.value;
  if (entry.cleared || leafSchema?.readOnly === true || /Ids?$/.test(key) || key === "id") return "readOnly";
  if (choicesOf(schema, entry.path).length > 0) return "choice";
  if (typeof value === "boolean") return "yesNo";
  if (typeof value === "number") return "number";
  if (isMoney(value)) return "money";
  if (typeof value !== "string") return "readOnly";
  if (UUID.test(value) || value.trimStart().startsWith('{"type":"doc"')) return "readOnly";
  if (ISO_DATE_ONLY.test(value)) return "date";
  if (ISO_DATE_TIME.test(value)) return "dateTime";
  return PROSE_KEY.test(key) || value.length > 80 || value.includes("\n") ? "longText" : "text";
};

export type CardEdit = { path: string[]; value: unknown };

export const isEmptyEdit = (value: unknown): boolean =>
  value === "" || value === undefined || value === null || (typeof value === "string" && value.trim() === "") ||
  (isMoney(value) && value.value === null);

export const editedValue = (edits: ReadonlyMap<string, CardEdit>, path: string[], proposed: unknown): unknown => {
  const key = path.join(".");
  return edits.has(key) ? edits.get(key)?.value : proposed;
};

export const withCardEdits = (message: string, editsLine: string | null): string =>
  editsLine ? `${message}\n\n${editsLine}` : message;

export const withEdits = (
  args: Record<string, unknown>,
  bodyName: string | undefined,
  edits: ReadonlyMap<string, CardEdit>,
): Record<string, unknown> => {
  if (!bodyName || edits.size === 0) return args;
  const body = structuredClone(args[bodyName]) as Record<string, unknown>;
  edits.forEach(({ path, value }) => {
    const parent = path.slice(0, -1).reduce<Record<string, unknown>>(
      (node, key) => node[key] as Record<string, unknown>,
      body,
    );
    parent[path[path.length - 1]] = value;
  });
  return { ...args, [bodyName]: body };
};

const PROCEDURE_PATH = "publicContext.procedureType";

export type Comparison = "none" | "loading" | "failed" | "ready";

export type ChoicesOnCard = { choices: string[]; draftRead: "loading" | "failed" | null };

export const choicesOnCard = (
  schema: Schema,
  path: string[],
  proposed: unknown,
  current: Record<string, unknown> | undefined,
  patch: Record<string, unknown>,
  comparison: Comparison,
): ChoicesOnCard => {
  if (path.join(".") !== PROCEDURE_PATH) return { choices: choicesOf(schema, path), draftRead: null };
  if (comparison === "failed") return { choices: withProposed(choicesOf(schema, path), proposed), draftRead: "failed" };
  const offered = current ? offeredProcedures(procedureInputsAfter(current, patch)) : [];
  return { choices: withProposed(offered, proposed), draftRead: comparison === "loading" ? "loading" : null };
};

const withProposed = (choices: readonly string[], proposed: unknown): string[] =>
  typeof proposed === "string" && !choices.includes(proposed) ? [...choices, proposed] : [...choices];

export const procedureInputsAfter = (current: Record<string, unknown>, patch: Record<string, unknown>): ProcedureInputs => {
  const context = {
    ...asRecord(current.eventContext),
    ...asRecord(patch.eventContext),
    ...asRecord(patch.publicContext),
  };
  const estimatedValue = asRecord(context.estimatedValue);
  return {
    regulation: context.regulation as ProcedureInputs["regulation"],
    foaPart: context.foaPart as ProcedureInputs["foaPart"],
    contractNature: context.contractNature as ProcedureInputs["contractNature"],
    serviceCategory: context.serviceCategory as ProcedureInputs["serviceCategory"],
    estimatedValueNok: typeof estimatedValue.value === "number" ? estimatedValue.value : null,
    announcedOnDoffin: context.announcedOnDoffin as ProcedureInputs["announcedOnDoffin"],
  };
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
